/**
 * 航班费用验证结果查看器
 *
 * 显示 FlightCostValidatorAgent 的验证结果，包括：
 * - 验证状态汇总（通过/错误比例）
 * - 按航班展示验证详情
 * - 高亮差异（标准答案 vs 提取结果）
 * - 产品级别的比对明细
 */
import React, { useState, useEffect, useMemo } from "react";
import { Modal, Typography, Card, Space, Button, Alert, Spin, Empty, Progress, Tag, Table, Tooltip, Collapse, Badge, Descriptions } from "antd";
import { SafetyCertificateOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, ReloadOutlined, CloseOutlined, ExclamationCircleOutlined, RocketOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useInvoiceReview } from "../../context/InvoiceReviewContext";
import AgentComparisonMatrix, { extractProductsMatrix } from "../../../components/AgentComparisonMatrix";

const { Text } = Typography;

// 状态配色映射
const STATUS_CONFIG = {
    PASSED: { color: "#52c41a", bg: "#f6ffed", icon: <CheckCircleOutlined />, text: "验证通过" },
    PARTIAL: { color: "#fa8c16", bg: "#fff7e6", icon: <ExclamationCircleOutlined />, text: "部分异常" },
    HAS_ERRORS: { color: "#ff4d4f", bg: "#fff2f0", icon: <CloseCircleOutlined />, text: "存在错误" },
    PENDING: { color: "#faad14", bg: "#fffbe6", icon: <ClockCircleOutlined />, text: "验证中" },
    NOT_STARTED: { color: "#8c8c8c", bg: "#fafafa", icon: <ClockCircleOutlined />, text: "未开始" },
};

const ValidationResultModal = ({ visible, onClose }) => {
    // 从 Context 获取共享的校验数据（Single Source of Truth）
    const { validationData, validationLoading: loading, loadValidationData } = useInvoiceReview();
    const [error] = useState(null); // 错误处理可按需保留

    // 手动刷新
    const handleRefresh = () => {
        loadValidationData();
    };

    // 解析验证结果
    const parsedResult = useMemo(() => {
        if (!validationData) return null;

        const { status, summary, validation_result, validation_session, progress } = validationData;

        // flight_results 可能是对象（以 flight_id 整数为 key）或数组
        // 需要统一转换为数组格式，便于表格渲染
        let flightResults = [];
        const rawFlightResults = validation_result?.flight_results;

        if (rawFlightResults) {
            if (Array.isArray(rawFlightResults)) {
                // 数组格式（来自单任务验证结果）
                // 单任务结果直接包含 products_comparison，需要构造 votes 兼容结构
                flightResults = rawFlightResults.map((item) => {
                    // 从 products_comparison 构造伪 votes 结构，供 renderProductDetails 使用
                    const productsComparison = item.products_comparison || {};
                    const pseudoVotes =
                        Object.keys(productsComparison).length > 0
                            ? [
                                  {
                                      task_id: "single",
                                      products: productsComparison,
                                  },
                              ]
                            : [];

                    return {
                        ...item,
                        flight_key: item.flight_key || {
                            flight_number: item.flight_number || "",
                            flight_date: item.flight_date || "",
                        },
                        // 统一 has_error 字段
                        has_error: item.has_error || false,
                        // 标准金额
                        standard_total: item.standard_total || 0,
                        // 提取金额：从 extracted_totals 获取，或使用 products_comparison 计算
                        extracted_totals: item.extracted_totals || [
                            Object.values(productsComparison).reduce((sum, p) => {
                                const vals = p.extracted || [];
                                return sum + (vals.find((v) => v > 0) ?? vals[0] ?? 0);
                            }, 0),
                        ],
                        // 错误原因
                        error_reason: item.error_reason || null,
                        // 保留 votes 用于产品级详情展示（如果已有则使用，否则构造伪结构）
                        votes: item.votes || pseudoVotes,
                        // 保留原始 products_comparison 用于渲染
                        products_comparison: productsComparison,
                    };
                });
            } else if (typeof rawFlightResults === "object") {
                // 对象格式（来自汇总结果）
                // V2.0: key 是 flight_id 整数，data 中包含 flight_number/flight_date
                // V1.0 兼容: key 可能是 "CT121|2025-12-02" 字符串
                flightResults = Object.entries(rawFlightResults).map(([flightKey, data]) => {
                    // 判断 key 类型：整数 ID 还是字符串 key
                    const isNumericKey = /^\d+$/.test(flightKey);

                    let flightNumber, flightDate;
                    if (isNumericKey) {
                        // V2.0: flight_id 作为 key，航班信息在 data 中
                        flightNumber = data.flight_number || "";
                        flightDate = data.flight_date || "";
                    } else {
                        // V1.0 兼容: "航班号|日期" 字符串
                        [flightNumber, flightDate] = flightKey.split("|");
                    }

                    return {
                        ...data,
                        flight_id: isNumericKey ? parseInt(flightKey, 10) : data.flight_id,
                        flight_key: {
                            flight_number: flightNumber,
                            flight_date: flightDate,
                        },
                        // 统一 has_error 字段：可能来自 status === "HAS_ERROR" 或 error_count > 0
                        has_error: data.status === "HAS_ERROR" || data.error_count > 0,
                        // 标准金额
                        standard_total: data.total,
                        // 提取金额：从 votes 中收集（每个 vote 是一个验证任务的结果）
                        extracted_totals: data.votes?.map((v) => v.total) || [],
                        // 错误原因：从 discrepancies 汇总
                        error_reason: data.discrepancies?.map((d) => d.reason).join("; ") || null,
                        // 保留 votes 用于产品级详情展示
                        votes: data.votes || [],
                    };
                });
            }
        }

        // 从 validation_session.tasks 中收集所有 execution_id（用于追溯）
        const executionIds = [];
        if (validation_session?.tasks) {
            validation_session.tasks.forEach((task) => {
                if (task.spidermind_execution_id) {
                    executionIds.push({
                        task_id: task.task_id,
                        execution_id: task.spidermind_execution_id,
                    });
                }
            });
        }
        // 也尝试直接从 validation_result 获取（单任务场景）
        if (validation_result?.spidermind_execution_id && executionIds.length === 0) {
            executionIds.push({
                task_id: "single",
                execution_id: validation_result.spidermind_execution_id,
            });
        }

        return {
            status,
            summary: summary || { total_flights: 0, passed_flights: 0, error_flights: 0 },
            flightResults,
            progress,
            validationSession: validation_session,
            validatedAt: validation_result?.validated_at,
            extractionAgent: validation_result?.extraction_agent,
            extractionCount: validation_result?.extraction_count,
            spidermindExecutionIds: executionIds,
        };
    }, [validationData]);

    // 状态配置
    const statusConfig = STATUS_CONFIG[parsedResult?.status] || STATUS_CONFIG.NOT_STARTED;

    // 航班结果表格列定义
    const flightColumns = [
        {
            title: "航班",
            dataIndex: "flight_key",
            key: "flight",
            width: 160,
            render: (flightKey, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{flightKey?.flight_number || "-"}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {flightKey?.flight_date || "-"}
                    </Text>
                    {record.flight_id && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            ID: {record.flight_id}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: "验证状态",
            dataIndex: "has_error",
            key: "status",
            width: 100,
            align: "center",
            render: (hasError) =>
                hasError ? (
                    <Tag color="error" icon={<CloseCircleOutlined />}>
                        异常
                    </Tag>
                ) : (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                        通过
                    </Tag>
                ),
        },
        {
            title: "标准金额",
            dataIndex: "standard_total",
            key: "standard_total",
            width: 120,
            align: "right",
            render: (val) => (
                <Text strong style={{ color: "#1890ff" }}>
                    {typeof val === "number" ? val.toLocaleString("zh-CN", { minimumFractionDigits: 2 }) : "-"}
                </Text>
            ),
        },
        {
            title: "提取金额",
            dataIndex: "extracted_totals",
            key: "extracted_totals",
            width: 140,
            align: "right",
            render: (totals, record) => {
                if (!Array.isArray(totals) || totals.length === 0) return "-";
                const standard = record.standard_total;
                return (
                    <Space direction="vertical" size={0}>
                        {totals.map((val, idx) => {
                            const isMatch = val === standard;
                            return (
                                <Text key={idx} style={{ color: isMatch ? "#52c41a" : "#ff4d4f" }}>
                                    {typeof val === "number" ? val.toLocaleString("zh-CN", { minimumFractionDigits: 2 }) : "-"}
                                    {!isMatch && <ExclamationCircleOutlined style={{ marginLeft: 4 }} />}
                                </Text>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: "错误原因",
            dataIndex: "error_reason",
            key: "error_reason",
            ellipsis: true,
            render: (reason) =>
                reason ? (
                    <Tooltip title={reason}>
                        <Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                            {reason}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
    ];

    // 渲染进度信息
    const renderProgress = () => {
        if (!parsedResult?.progress) return null;
        const { total_tasks, completed_tasks, percentage } = parsedResult.progress;

        return (
            <Card size="small" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Space>
                        <ClockCircleOutlined style={{ color: "#faad14" }} />
                        <Text>验证进行中...</Text>
                    </Space>
                    <Progress percent={percentage} status="active" strokeColor={{ "0%": "#108ee9", "100%": "#87d068" }} />
                    <Text type="secondary">
                        已完成 {completed_tasks} / {total_tasks} 个验证任务
                    </Text>
                </Space>
            </Card>
        );
    };

    // 渲染验证汇总
    const renderSummary = () => {
        if (!parsedResult?.summary) return null;
        // 兼容后端字段名：errors -> error_flights, passed -> passed_flights
        const rawSummary = parsedResult.summary;
        const total_flights = rawSummary.total_flights || 0;
        const passed_flights = rawSummary.passed_flights ?? rawSummary.passed ?? 0;
        const error_flights = rawSummary.error_flights ?? rawSummary.errors ?? 0;
        const failed_candidate_count = rawSummary.failed_candidate_count || 0;
        const passRate = total_flights > 0 ? Math.round((passed_flights / total_flights) * 100) : 0;

        return (
            <Card
                size="small"
                style={{
                    marginBottom: 16,
                    background: statusConfig.bg,
                    borderColor: statusConfig.color,
                }}
            >
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                    <Space size="large">
                        <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 28, fontWeight: 600, color: statusConfig.color }}>{statusConfig.icon}</div>
                            <Text style={{ color: statusConfig.color }}>{statusConfig.text}</Text>
                        </div>

                        <div style={{ borderLeft: "1px solid #e8e8e8", paddingLeft: 24 }}>
                            <Space size="large">
                                <Tooltip title="总航班数">
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: 24, fontWeight: 600 }}>{total_flights}</div>
                                        <Text type="secondary">总航班</Text>
                                    </div>
                                </Tooltip>
                                <Tooltip title="验证通过">
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: 24, fontWeight: 600, color: "#52c41a" }}>{passed_flights}</div>
                                        <Text type="secondary">通过</Text>
                                    </div>
                                </Tooltip>
                                <Tooltip title="存在错误">
                                    <div style={{ textAlign: "center" }}>
                                        <div style={{ fontSize: 24, fontWeight: 600, color: error_flights > 0 ? "#ff4d4f" : "#8c8c8c" }}>{error_flights}</div>
                                        <Text type="secondary">错误</Text>
                                    </div>
                                </Tooltip>
                            </Space>
                        </div>
                    </Space>

                    <Progress type="circle" percent={passRate} width={60} strokeColor={passRate === 100 ? "#52c41a" : passRate >= 80 ? "#faad14" : "#ff4d4f"} format={(pct) => `${pct}%`} />
                </Space>
                {failed_candidate_count > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                        message={`有 ${failed_candidate_count} 个验证 Agent 执行失败，已按未匹配结果计入最终汇总`}
                    />
                )}
            </Card>
        );
    };

    // 渲染产品级别比对详情（展开行内容）- V6.0 使用 AgentComparisonMatrix
    const renderProductDetails = (record) => {
        // 优先使用后端 V6.0 的 products_matrix，降级到从 votes/products_comparison 构建
        const productsMatrix = extractProductsMatrix(record);
        const votes = record.votes || [];

        if (!productsMatrix) {
            return <Empty description="无产品详情" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        // 收集 Agent 原始提取的产品信息（用于追溯）
        const extractedRawProducts = [];
        votes.forEach((vote, idx) => {
            if (vote.extracted_products_raw && vote.extracted_products_raw.length > 0) {
                vote.extracted_products_raw.forEach((rawProducts) => {
                    if (rawProducts && Object.keys(rawProducts).length > 0) {
                        extractedRawProducts.push({
                            task_id: vote.task_id || `验证${idx + 1}`,
                            execution_id: vote.execution_id,
                            products: rawProducts,
                        });
                    }
                });
            }
        });

        // 检查是否有任何错误
        const hasAnyError = Object.values(productsMatrix).some((p) => !p.match);

        return (
            <div style={{ padding: "8px 16px", background: "#fafafa" }}>
                <AgentComparisonMatrix productsMatrix={productsMatrix} compact={false} voteCount={record.vote_count || votes.length} />

                {/* 当有错误时，显示 Agent 原始提取结果用于追溯 */}
                {hasAnyError && extractedRawProducts.length > 0 && (
                    <Collapse ghost style={{ marginTop: 12 }}>
                        <Collapse.Panel
                            header={
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <RocketOutlined style={{ marginRight: 4 }} />
                                    查看 Agent 原始提取结果（用于追溯）
                                </Text>
                            }
                            key="raw"
                        >
                            <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                {extractedRawProducts.map((item, idx) => (
                                    <Card key={idx} size="small" style={{ background: "#fff8e6" }}>
                                        <Space direction="vertical" size={4}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {item.task_id}
                                                {item.execution_id && (
                                                    <Text code copyable style={{ fontSize: 10, marginLeft: 8 }}>
                                                        {item.execution_id}
                                                    </Text>
                                                )}
                                            </Text>
                                            <div>
                                                {Object.entries(item.products).map(([name, amount]) => (
                                                    <Tag key={name} color="orange" style={{ marginBottom: 4 }}>
                                                        {name}: {typeof amount === "number" ? amount.toLocaleString("zh-CN", { minimumFractionDigits: 2 }) : amount}
                                                    </Tag>
                                                ))}
                                            </div>
                                        </Space>
                                    </Card>
                                ))}
                            </Space>
                        </Collapse.Panel>
                    </Collapse>
                )}
            </div>
        );
    };

    // 表格展开配置
    const expandableConfig = {
        expandedRowRender: renderProductDetails,
        rowExpandable: (record) => (record.votes && record.votes.length > 0) || record.products_matrix || record.products_comparison,
    };

    // 渲染航班详情
    const renderFlightDetails = () => {
        // 确保 flightResults 是数组
        const flightResults = Array.isArray(parsedResult?.flightResults) ? parsedResult.flightResults : [];

        if (flightResults.length === 0) {
            return <Empty description="暂无验证航班详情" />;
        }

        // 分离通过和错误的航班
        const errorFlights = flightResults.filter((f) => f.has_error);
        const passedFlights = flightResults.filter((f) => !f.has_error);

        return (
            <Collapse defaultActiveKey={errorFlights.length > 0 ? ["errors"] : ["passed"]} ghost>
                {errorFlights.length > 0 && (
                    <Collapse.Panel
                        header={
                            <Space>
                                <Badge status="error" />
                                <Text strong style={{ color: "#ff4d4f" }}>
                                    错误航班 ({errorFlights.length})
                                </Text>
                            </Space>
                        }
                        key="errors"
                    >
                        <Table columns={flightColumns} dataSource={errorFlights} rowKey={(r) => r.flight_id || r.flight_key?.flight_number} pagination={false} size="small" scroll={{ x: 700 }} expandable={expandableConfig} />
                    </Collapse.Panel>
                )}

                {passedFlights.length > 0 && (
                    <Collapse.Panel
                        header={
                            <Space>
                                <Badge status="success" />
                                <Text style={{ color: "#52c41a" }}>通过航班 ({passedFlights.length})</Text>
                            </Space>
                        }
                        key="passed"
                    >
                        <Table columns={flightColumns} dataSource={passedFlights} rowKey={(r) => r.flight_id || r.flight_key?.flight_number} pagination={false} size="small" scroll={{ x: 700 }} expandable={expandableConfig} />
                    </Collapse.Panel>
                )}
            </Collapse>
        );
    };

    // 渲染元数据
    const renderMetadata = () => {
        if (!parsedResult) return null;

        const { spidermindExecutionIds = [] } = parsedResult;

        return (
            <Descriptions size="small" column={2} style={{ marginTop: 16 }}>
                {parsedResult.extractionAgent && <Descriptions.Item label="验证Agent">{parsedResult.extractionAgent}</Descriptions.Item>}
                {parsedResult.extractionCount && <Descriptions.Item label="提取次数">{parsedResult.extractionCount} 次</Descriptions.Item>}
                {parsedResult.validatedAt && <Descriptions.Item label="验证时间">{new Date(parsedResult.validatedAt).toLocaleString()}</Descriptions.Item>}
                {spidermindExecutionIds.length > 0 && (
                    <Descriptions.Item label="SpiderMind Execution ID" span={2}>
                        <Space direction="vertical" size={2}>
                            {spidermindExecutionIds.map((item, idx) => (
                                <Text key={idx} code copyable style={{ fontSize: 12 }}>
                                    {item.task_id !== "single" && `[${item.task_id}] `}
                                    {item.execution_id}
                                </Text>
                            ))}
                        </Space>
                    </Descriptions.Item>
                )}
            </Descriptions>
        );
    };

    // 渲染主内容
    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin tip="正在加载验证结果..." />
                </div>
            );
        }

        if (error) {
            return <Alert message="加载失败" description={error} type="error" showIcon action={<Button size="small" onClick={loadValidationData} icon={<ReloadOutlined />} />} />;
        }

        if (!validationData?.has_validation) {
            return (
                <Empty
                    image={<RocketOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />}
                    description={
                        <Space direction="vertical" size={4}>
                            <Text>暂无验证数据</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                验证将在费用分摊完成后自动触发
                            </Text>
                        </Space>
                    }
                />
            );
        }

        return (
            <div>
                {/* 进度（如果正在验证中） */}
                {parsedResult?.status === "PENDING" && renderProgress()}

                {/* 汇总卡片 */}
                {renderSummary()}

                {/* 航班详情 */}
                <Card size="small" title="验证航班详情" style={{ marginTop: 16 }}>
                    {renderFlightDetails()}
                </Card>

                {/* 元数据 */}
                {renderMetadata()}
            </div>
        );
    };

    return (
        <Modal
            title={
                <Space>
                    <SafetyCertificateOutlined style={{ color: "#1890ff" }} />
                    <span>航班费用验证结果</span>
                    {validationData?.has_validation && (
                        <Tag color={statusConfig.color} style={{ marginLeft: 8 }}>
                            {statusConfig.text}
                        </Tag>
                    )}
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={900}
            className="large-modal"
            footer={[
                <Button key="refresh" icon={<ReloadOutlined />} onClick={handleRefresh} disabled={loading} style={{ float: "left" }}>
                    刷新
                </Button>,
                <Button key="close" icon={<CloseOutlined />} onClick={onClose}>
                    关闭 (ESC)
                </Button>,
            ]}
            destroyOnClose
            style={{ top: 40 }}
        >
            <div style={{ maxHeight: "70vh", overflow: "auto", paddingRight: 4 }}>{renderContent()}</div>
        </Modal>
    );
};

export default ValidationResultModal;
